import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { getInquiries, getStats, migrateDisplayNames, migrateRentalStatus, clearShortStayOwnership } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [migrating, setMigrating] = useState(false);
  const [migratingRental, setMigratingRental] = useState(false);
  const [clearingShortStay, setClearingShortStay] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, inquiriesRes] = await Promise.all([
          getStats(),
          getInquiries({ status: 'new' }),
        ]);
        setStats(statsRes.data);
        setRecentInquiries(inquiriesRes.data.slice(0, 5));
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Dashboard fetch failed:', err);
      }
    };
    fetchData();
  }, []);

  const runDisplayNameMigration = async () => {
    if (!confirm('Run one-time migration: generate public display names for any properties missing them. Safe to run multiple times.')) return;
    setMigrating(true);
    try {
      const res = await migrateDisplayNames();
      const { ids_backfilled, display_names_generated, listing_codes_generated } = res.data;
      const total = (ids_backfilled || 0) + (display_names_generated || 0) + (listing_codes_generated || 0);
      if (total === 0) {
        toast.success('All properties already migrated. Nothing to do.');
      } else {
        const parts = [];
        if (display_names_generated) parts.push(`${display_names_generated} display names`);
        if (listing_codes_generated) parts.push(`${listing_codes_generated} listing codes`);
        if (ids_backfilled) parts.push(`${ids_backfilled} IDs`);
        toast.success(`Migrated ${parts.join(', ')}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const runRentalStatusMigration = async () => {
    if (!confirm('Run one-time migration: convert legacy "for_rent" status to "for_rent_short" so those properties show up on /stays with the "Managed by Lalita" badge. Safe to re-run.')) return;
    setMigratingRental(true);
    try {
      const res = await migrateRentalStatus();
      const { updated } = res.data;
      if (updated === 0) {
        toast.success('No legacy rental statuses found. Nothing to do.');
      } else {
        toast.success(`Migrated ${updated} propert${updated === 1 ? 'y' : 'ies'} to short-term rental.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigratingRental(false);
    }
  };

  const runClearShortStayOwnership = async () => {
    if (!confirm('Clear "Ownership Type" on all Short Stay properties? They are not for sale, so Freehold/Leasehold/Yearly Rental do not apply. Safe to re-run.')) return;
    setClearingShortStay(true);
    try {
      const res = await clearShortStayOwnership();
      const { updated } = res.data;
      if (updated === 0) {
        toast.success('All short stays are already clean. Nothing to do.');
      } else {
        toast.success(`Cleared ownership_type on ${updated} short stay${updated === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setClearingShortStay(false);
    }
  };

  const statCards = [
    { label: 'Total Properties', value: stats?.total_properties || 0, color: 'bg-ubud-jungle' },
    { label: 'For Sale', value: stats?.for_sale || 0, color: 'bg-temple-gold' },
    { label: 'Managed by Lalita', value: stats?.villas_managed || 0, color: 'bg-blue-500' },
    { label: 'Long-term Rental', value: stats?.long_term_rental || 0, color: 'bg-charcoal-light' },
  ];

  return (
    <div data-testid="admin-overview">
      <h1 className="font-heading text-2xl font-semibold text-charcoal mb-6">Dashboard Overview</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm">
            <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="font-heading text-2xl font-semibold text-charcoal">{stat.value}</div>
            <div className="text-charcoal-muted text-sm">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Inquiries */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-medium text-charcoal">Recent Inquiries</h2>
          <Link to="/admin/inquiries" className="text-ubud-jungle text-sm hover:underline">View All</Link>
        </div>
        {recentInquiries.length === 0 ? (
          <p className="text-charcoal-muted text-sm">No new inquiries</p>
        ) : (
          <div className="space-y-3">
            {recentInquiries.map((inq) => (
              <div key={inq.id} className="flex items-center justify-between p-3 bg-warm-sand rounded-lg">
                <div>
                  <div className="font-medium text-charcoal">{inq.name}</div>
                  <div className="text-charcoal-muted text-sm">{inq.email}</div>
                </div>
                <span className="text-xs bg-ubud-jungle text-white px-2 py-1 rounded-full">
                  {inq.inquiry_type}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Maintenance */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
        <h2 className="font-heading text-lg font-medium text-charcoal mb-1">Maintenance</h2>
        <p className="text-charcoal-muted text-sm mb-4">One-time tasks for data hygiene. Safe to re-run.</p>
        <div className="space-y-3">
          <div className="border border-gray-100 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[260px]">
              <div className="font-medium text-charcoal">Generate Public Display Names</div>
              <div className="text-charcoal-muted text-sm">
                Auto-generates names like "3-Bedroom Villa in Canggu" for any property missing one.
                Also backfills internal IDs for legacy records.
              </div>
            </div>
            <Button
              onClick={runDisplayNameMigration}
              disabled={migrating}
              className="bg-ubud-jungle hover:bg-ubud-jungle-dark"
              data-testid="migrate-display-names-btn"
            >
              {migrating ? 'Running…' : 'Run Migration'}
            </Button>
          </div>
          <div className="border border-gray-100 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[260px]">
              <div className="font-medium text-charcoal">Fix Legacy Rental Status</div>
              <div className="text-charcoal-muted text-sm">
                Converts properties saved with the old <code className="text-xs">for_rent</code> status to
                {' '}<code className="text-xs">for_rent_short</code> so they appear on the Stays page with the
                "Managed by Lalita" badge.
              </div>
            </div>
            <Button
              onClick={runRentalStatusMigration}
              disabled={migratingRental}
              className="bg-ubud-jungle hover:bg-ubud-jungle-dark"
              data-testid="migrate-rental-status-btn"
            >
              {migratingRental ? 'Running…' : 'Run Migration'}
            </Button>
          </div>
          <div className="border border-gray-100 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[260px]">
              <div className="font-medium text-charcoal">Clear Short-Stay Ownership</div>
              <div className="text-charcoal-muted text-sm">
                Short-term stays are not for sale, so they shouldn't carry an Ownership Type
                (Freehold / Leasehold / Yearly Rental). Clears stale values on existing short-stay listings.
              </div>
            </div>
            <Button
              onClick={runClearShortStayOwnership}
              disabled={clearingShortStay}
              className="bg-ubud-jungle hover:bg-ubud-jungle-dark"
              data-testid="clear-shortstay-ownership-btn"
            >
              {clearingShortStay ? 'Running…' : 'Run Cleanup'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
